import React, { createContext, useContext, useMemo } from 'react';

import {
  RichTextKeyboardChromeContext,
  RichTextKeyboardChromeProvider,
  dismissActiveRichTextKeyboard,
  useRegisterRichTextKeyboardDismiss,
  useRichTextKeyboardBarVisible,
  useRichTextKeyboardDismissBar,
  type RichTextKeyboardDismissFn,
} from './RichTextKeyboardChromeContext';

import type { ScrollView } from 'react-native';

export type { RichTextKeyboardDismissFn };
export {
  dismissActiveRichTextKeyboard,
  useRegisterRichTextKeyboardDismiss,
  useRichTextKeyboardBarVisible,
  useRichTextKeyboardDismissBar,
};

export type FormKeyboardScrollContextValue = {
  scrollRef: React.RefObject<ScrollView | null>;
  bottomOffset: number;
};

export const FormKeyboardScrollContext =
  createContext<FormKeyboardScrollContextValue | null>(null);

function FormKeyboardScrollInner({
  children,
  scrollRef,
  bottomOffset,
}: {
  children: React.ReactNode;
  scrollRef: React.RefObject<ScrollView | null>;
  bottomOffset: number;
}): React.JSX.Element {
  const value = useMemo(
    () => ({
      scrollRef,
      bottomOffset,
    }),
    [scrollRef, bottomOffset],
  );

  return (
    <FormKeyboardScrollContext.Provider value={value}>
      {children}
    </FormKeyboardScrollContext.Provider>
  );
}

export function FormKeyboardScrollProvider({
  children,
  scrollRef,
  bottomOffset,
  isolateChrome = false,
}: {
  children: React.ReactNode;
  scrollRef: React.RefObject<ScrollView | null>;
  bottomOffset: number;
  /**
   * Native unit-editor modals sit above the request form. They need their own
   * Done-bar chrome or the overlay stays behind the Modal (1106 issue 26).
   */
  isolateChrome?: boolean;
}): React.JSX.Element {
  const existingChrome = useContext(RichTextKeyboardChromeContext);

  const inner = (
    <FormKeyboardScrollInner scrollRef={scrollRef} bottomOffset={bottomOffset}>
      {children}
    </FormKeyboardScrollInner>
  );

  if (existingChrome && !isolateChrome) {
    return inner;
  }

  return (
    <RichTextKeyboardChromeProvider>{inner}</RichTextKeyboardChromeProvider>
  );
}
